// ManualPickModal — the "barcode not scanning" fallback for the
// directed-pick screens (picking waves + shelf/forecast drop).
//
// Opened from the hand button in PickingScreen's dark action bar. Shows
// the HERO item only — manual pick is deliberately pinned to the item
// the screen is telling the picker to pick; anything else at the
// location waits until it's up. The picker confirms a quantity —
// defaulting to 1 and capped at the line's remaining units — and the
// parent submits it as if that many scans had been accepted (waves:
// POST pick-item-manual; drop: the same local accept path as a scan).
import { useState } from "react";
import { Minus, Package, Plus, X } from "lucide-react";
import { useTranslation } from "../../i18n";
import "./ManualPickModal.css";

export interface ManualPickItem {
    sku: string;
    title: string | null;
    thumbnailUrl: string | null;
    imageUrl: string | null;
    /** Units left to pick on the hero line — the hard cap. */
    maxQty: number;
}

interface Props {
    item: ManualPickItem;
    busy: boolean;
    onConfirm: (qty: number) => void;
    onClose: () => void;
}

const ManualPickModal = ({ item, busy, onConfirm, onClose }: Props) => {
    const { t } = useTranslation();
    // Default to a single unit. Manual pick is the damaged-barcode
    // fallback, so the safe default is the smallest commitment: the
    // picker steps up to a multi-unit pick deliberately rather than
    // confirming the whole line by reflex.
    const [qty, setQty] = useState(1);

    const step = (delta: number) =>
        setQty(q => Math.min(Math.max(1, item.maxQty), Math.max(1, q + delta)));

    const src = item.thumbnailUrl || item.imageUrl;

    return (
        <div className="modal-overlay" onClick={() => { if (!busy) onClose(); }}>
            <div className="modal-card manual-pick-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-card-header">
                    <h2>{t("picking_manual_title")}</h2>
                    <button className="modal-close" onClick={onClose} disabled={busy} aria-label={t("cancel")}>
                        <X size={18} />
                    </button>
                </div>
                <div className="modal-card-body">
                    <div className="manual-pick-hint">{t("picking_manual_hint")}</div>
                    <div className="manual-pick-item">
                        <span className="manual-pick-thumb" aria-hidden="true">
                            {src ? (
                                <img
                                    src={src}
                                    alt=""
                                    loading="lazy"
                                    onError={e => { e.currentTarget.style.display = "none"; }}
                                />
                            ) : (
                                <Package size={22} />
                            )}
                        </span>
                        <span className="manual-pick-info">
                            {item.title && <span className="manual-pick-title">{item.title}</span>}
                            <span className="manual-pick-sku">{item.sku}</span>
                        </span>
                    </div>
                    <div className="manual-pick-qty-label">{t("picking_manual_qty_label")}</div>
                    <div className="manual-pick-stepper">
                        <button
                            type="button"
                            className="manual-pick-step"
                            onClick={() => step(-1)}
                            disabled={busy || qty <= 1}
                            aria-label="-1"
                        >
                            <Minus size={22} />
                        </button>
                        <span className="manual-pick-qty" role="status">{qty}</span>
                        <button
                            type="button"
                            className="manual-pick-step"
                            onClick={() => step(1)}
                            disabled={busy || qty >= item.maxQty}
                            aria-label="+1"
                        >
                            <Plus size={22} />
                        </button>
                    </div>
                    <div className="manual-pick-max">{t("picking_manual_max", { max: String(item.maxQty) })}</div>
                    <div className="modal-confirm-buttons manual-pick-actions">
                        <button className="btn btn-ghost btn-lg" onClick={onClose} disabled={busy}>
                            {t("cancel")}
                        </button>
                        <button
                            className="btn btn-primary btn-lg"
                            onClick={() => onConfirm(qty)}
                            disabled={busy || qty < 1 || qty > item.maxQty}
                        >
                            {busy ? t("submitting") : t("picking_manual_confirm")}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ManualPickModal;
